import { FunctionComponent, useEffect, useState } from 'react';
import {
    Box,
    Button,
    IconButton,
    Stack,
    SvgIcon,
    Typography,
    useTheme,
} from '@mui/material';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useGetCardCodesQuery } from '@/api/v2/cardCodes';
import { StarjackTable } from '@starjack/shared-components';
import { usePagination, FormDrawer } from '@starjack/shared-components';
import { CardCode } from '@/models/cardcodes';
import Details from '@heroicons/react/24/solid/DocumentMagnifyingGlassIcon';
import Link from '@mui/material/Link';
import { useNavigate } from 'react-router-dom';
import { useSlice } from '@/hooks/useSlice';
import { pageDataSlice } from '@starjack/features/pageData';
import { IntroText } from '@/pages/specialTariffs/components/IntroText';
import { NewSpecialTarifCard } from '@/pages/specialTariffs/components/NewSpecialTarifCard';

type SpecialTarifsContentProps = {
    isSpecialTarifsNew: boolean;
};
export const SpecialTarifsContent: FunctionComponent<
    SpecialTarifsContentProps
> = ({ isSpecialTarifsNew }) => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();

    const { data, isLoading, error } = useGetCardCodesQuery({ liftOperatorId });
    const { currentItems, setNextPage, itemsPerPage, setItemsPerPage } =
        usePagination({
            items: data,
            itemsPerPage: 10,
        });
    const theme = useTheme();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const {
        actions: { setPageData },
    } = useSlice(pageDataSlice, 'requestSlice');

    const toggleSpecialTarifsNewDrawer = () => {
        if (isSpecialTarifsNew) {
            setIsOpen(false);

            setTimeout(() => {
                navigate('/spezialtarife');
            }, 100);
        } else {
            navigate('/spezialtarife/neu');
        }
    };

    useEffect(() => {
        if (isSpecialTarifsNew) {
            setTimeout(() => {
                setIsOpen(true);
            }, 100);
        }

        setPageData({
            title: 'Spezialtarife',
            body: <IntroText />,
        });
    }, []);

    return (
        <>
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                Spezialtarife
            </Typography>

            <Stack spacing={theme.page.verticalSpacing}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        width: '100%',
                        p: 3,
                    }}
                >
                    <Button
                        onClick={toggleSpecialTarifsNewDrawer}
                        variant='contained'
                    >
                        + Neuen Spezialtarif anlegen
                    </Button>
                </Box>

                <FormDrawer
                    anchor='right'
                    open={isOpen}
                    onClose={toggleSpecialTarifsNewDrawer}
                >
                    <NewSpecialTarifCard
                        onClose={toggleSpecialTarifsNewDrawer}
                    />
                </FormDrawer>

                <StarjackTable<CardCode>
                    count={data?.length}
                    items={currentItems}
                    columns={[
                        {
                            label: 'Details',
                            itemTransform: (item) => (
                                <Link
                                    href={`/spezialtarife/details/?id=${item.id}`}
                                >
                                    <IconButton aria-label='Details anzeigen'>
                                        <SvgIcon>
                                            <Details />
                                        </SvgIcon>
                                    </IconButton>
                                </Link>
                            ),
                            options: {
                                rowCellProps: {
                                    sx: {
                                        width: '80px',
                                    },
                                },
                            },
                        },
                        {
                            label: 'Name der Freigabegruppe',
                            itemKey: 'externalCode',
                        },
                        {
                            label: 'Anzahl der Freigabe-Codes',
                            itemTransform: (item) => item.singleCodes.length,
                            options: {
                                headerCellProps: {
                                    align: 'right',
                                },
                                rowCellProps: {
                                    align: 'right',
                                },
                            },
                        },
                    ]}
                    itemId={'id'}
                    loading={isLoading}
                    error={error}
                    onRowsPerPageChange={setItemsPerPage}
                    rowsPerPage={itemsPerPage}
                    onPageChange={setNextPage}
                />
            </Stack>
        </>
    );
};
